import { useEffect } from "react";
import { useLoaderData, useLocation, useNavigate, useSearchParams } from "@remix-run/react";
import { useInView } from "react-intersection-observer";
import { useScrollToAnchor } from "~/hooks";
import { SearchProvider } from "~/context/SearchContext";

// Layout Components
import Header from "~/components/Header";
import Footer from "~/components/Footer";
import TopicPath from "~/components/TopicPath";
import Contact2 from "~/components/top/Contact2";

// List-specific Components
import Sidebar from "~/components/list/sidebar/Sidebar";
import SelectedArea from "~/components/list/SelectedArea";
import Pagination from "~/components/list/Pagination";
import SpFixedArea from "~/components/list/SpFixedArea";
import SpSearchButton from "~/components/list/SpSearchButton";
import { ResultsList } from "~/components/list/ResultsList";
import { SortSelect } from "~/components/list/SortSelect";

// Types
import { SortOptions, type MastersAndJobs, type SortOption } from "~/types";
import { loader } from "./loader";

export { meta } from "./meta";
export { loader } from "./loader";

export default function List() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { scrollToAnchor } = useScrollToAnchor({ smooth: true });
  const { ref, inView } = useInView({
    root: null,
    rootMargin: "-1% 0% -99% 0%",
    threshold: 0,
  });

  const mastersAndJobs = useLoaderData<MastersAndJobs>();
  const {
    currentEnvironment,
    pagingJobs,
    jobsCount,
    currentPage,
    pageSize,
    searchAreaCategories,
    searchAreas,
    serviceTypeCategories,
    serviceTypes,
    occupationTypeCategories,
    occupationTypes,
    parameterOccupationTypeIds,
    parameterSearchAreaIds,
    parameterServiceTypeIds,
    parameterFreeWord,
  } = mastersAndJobs;

  const currentSort =
    (searchParams.get("sort") as SortOption) || SortOptions.NEWEST;

  const handleSortChange = (newSort: SortOption) => {
    const params = new URLSearchParams(searchParams);
    params.set("sort", newSort);
    navigate(`${location.pathname}?${params.toString()}`);
  };

  useEffect(() => {
    scrollToAnchor();
  }, [scrollToAnchor]);

  const masters = {
    searchAreaCategories,
    searchAreas,
    serviceTypeCategories,
    serviceTypes,
    occupationTypeCategories,
    occupationTypes,
  };

  const initialConditions = {
    occupation_type_ids: parameterOccupationTypeIds,
    search_area_ids: parameterSearchAreaIds,
    service_type_ids: parameterServiceTypeIds,
  };

  const pagingJobsCount = pagingJobs.length;
  const totalPages = Math.ceil(jobsCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, jobsCount);

  const getAreaTitle = () => {
    if (parameterSearchAreaIds.length === 1) {
      const area = searchAreas.find((a) => a.id === parameterSearchAreaIds[0]);
      if (area) {
        if (area.id === 24) {
          return "町田市・立川市・八王子市等ほかエリアの";
        }
        return `${area.name}の`;
      }
    } else if (
      parameterSearchAreaIds.length === 24 &&
      parameterSearchAreaIds.every((id) => Number(id) >= 1 && Number(id) <= 24)
    ) {
      return "東京都の";
    }
    return "";
  };

  return (
    <>
      <SearchProvider initialConditions={initialConditions}>
        <div>
          <Header />
          <TopicPath>
            <li>
              <a href="/">TOP</a>
            </li>
            <li>求人一覧</li>
          </TopicPath>
          <main ref={ref}>
            <div className="px-4 mt-14 pb-20 md:px-7 md:pb-[104px]">
              <div className="md:grid md:grid-cols-[1fr_238px] md:gap-10 md:max-w-[1040px] md:mx-auto">
                <section>
                  <h1 className="mb-8 text-center text-[32px] leading-normal font-black md:text-5xl md:leading-normal">
                    {getAreaTitle()}求人一覧
                  </h1>
                  <div className="my-8">
                    <SelectedArea masters={masters} />
                  </div>
                  <div className="md:hidden flex justify-center mt-8 mb-14 [&>button]:w-[280px]">
                    <SpSearchButton masters={masters} />
                  </div>

                  <div className="flex items-center justify-between mt-8 mb-6">
                    <p className="text-sm font-bold leading-normal">
                      <strong className="mr-1 text-2xl text-primary font-bold">
                        {jobsCount.toLocaleString()}
                      </strong>
                      件中{" "}
                      <strong className="mx-1 text-lg font-bold">
                        {pagingJobsCount === 0
                          ? "0"
                          : `${startItem}~${endItem}`}
                      </strong>
                      件表示
                    </p>
                    <SortSelect
                      value={currentSort}
                      onChange={handleSortChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-6">
                    <ResultsList
                      pagingJobs={pagingJobs}
                      masters={masters}
                      currentEnvironment={currentEnvironment}
                    />
                  </div>

                  {pagingJobsCount !== 0 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                    />
                  )}
                </section>

                <aside>
                  <Sidebar masters={masters} />
                </aside>
              </div>
            </div>
            <Contact2 />
          </main>
          <Footer />
          {inView && <SpFixedArea masters={masters} />}
        </div>
      </SearchProvider>
    </>
  );
}
